import React, { useState } from 'react';
import { createUserWithEmailAndPassword, signInWithEmailAndPassword, signInWithPopup } from 'firebase/auth';
import { doc, setDoc, getDoc } from 'firebase/firestore';
import { auth, db, googleProvider } from '../firebase';
import { useAuth } from '../contexts/AuthContext';

function SignUp({ onSignInClick, onSignUpSuccess }) {
  const { fetchUserProfile } = useAuth();
  const [isSignUp, setIsSignUp] = useState(true);
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    confirmPassword: '',
    name: '',
    phone: '',
    location: '',
    userType: 'buyer',
    sellerType: 'individual',
    // Additional seller fields
    website: '',
    facebook: '',
    instagram: '',
    businessDescription: '',
    yearsInBusiness: '',
    specializations: ''
  });
  const [loading, setLoading] = useState(false);
  const [googleLoading, setGoogleLoading] = useState(false);
  const [error, setError] = useState('');

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    setError(''); // Clear error when user types
  };

  const validateForm = () => {
    if (!formData.email || !formData.password || !formData.name || !formData.phone || !formData.location) {
      setError('Please fill in all required fields');
      return false;
    }
    
    if (formData.password.length < 6) {
      setError('Password must be at least 6 characters long');
      return false;
    }
    
    if (isSignUp && formData.password !== formData.confirmPassword) {
      setError('Passwords do not match');
      return false;
    }
    
    if (!formData.email.includes('@')) {
      setError('Please enter a valid email address');
      return false;
    }
    
    // Only require business description for sellers
    if (isSignUp && formData.userType === 'seller' && !formData.businessDescription) {
      setError('Please provide a business description');
      return false;
    }
    
    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) return;
    
    setLoading(true);
    setError('');
    
    try {
      if (isSignUp) {
        // Sign up
        const userCredential = await createUserWithEmailAndPassword(
          auth, 
          formData.email, 
          formData.password
        );
        
        // Save user data to Firestore
        const userData = {
          name: formData.name,
          email: formData.email,
          phone: formData.phone,
          location: formData.location,
          userType: formData.userType,
          sellerType: formData.userType === 'seller' ? formData.sellerType : null,
          createdAt: new Date().toISOString(),
          updatedAt: new Date().toISOString()
        };

        // If seller, add additional fields
        if (formData.userType === 'seller') {
          userData.website = formData.website;
          userData.facebook = formData.facebook;
          userData.instagram = formData.instagram;
          userData.businessDescription = formData.businessDescription;
          userData.yearsInBusiness = formData.yearsInBusiness;
          userData.specializations = formData.specializations;
          userData.approved = false; // Seller needs approval
        }

        await setDoc(doc(db, 'users', userCredential.user.uid), userData);

        // If seller, create seller review entry
        if (formData.userType === 'seller') {
          await setDoc(doc(db, 'sellerReviews', userCredential.user.uid), {
            userId: userCredential.user.uid,
            name: formData.name,
            email: formData.email,
            phone: formData.phone,
            location: formData.location,
            sellerType: formData.sellerType,
            website: formData.website,
            facebook: formData.facebook,
            instagram: formData.instagram,
            businessDescription: formData.businessDescription,
            yearsInBusiness: formData.yearsInBusiness,
            specializations: formData.specializations,
            status: 'pending',
            submittedAt: new Date().toISOString(),
            reviewedAt: null,
            reviewedBy: null,
            notes: ''
          });
        }
        
        await fetchUserProfile(userCredential.user);
        // The AuthContext will automatically update and redirect to marketplace
        onSignUpSuccess();
              } else {
          // Sign in
          const userCredential = await signInWithEmailAndPassword(auth, formData.email, formData.password);
          await fetchUserProfile(userCredential.user);
          // The AuthContext will automatically update and redirect to marketplace
          onSignUpSuccess();
        }
    } catch (error) {
      console.error('Auth error:', error);
      switch (error.code) {
        case 'auth/email-already-in-use':
          setError('An account with this email already exists. Please sign in instead.');
          break;
        case 'auth/user-not-found':
          setError('No account found with this email. Please sign up first.');
          break;
        case 'auth/wrong-password':
          setError('Incorrect password. Please try again.');
          break;
        case 'auth/weak-password':
          setError('Password is too weak. Please choose a stronger password.');
          break;
        case 'auth/invalid-email':
          setError('Invalid email address.');
          break;
        default:
          setError('An error occurred. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleGoogleSignIn = async () => {
    setGoogleLoading(true);
    setError('');
    
    try {
      const result = await signInWithPopup(auth, googleProvider);
      const user = result.user;
      
      // Check if user already exists in Firestore
      const userDoc = await getDoc(doc(db, 'users', user.uid));
      
              if (!userDoc.exists()) {
          // New user - prompt for additional info
          const additionalInfo = await promptForAdditionalInfo(user);
          if (additionalInfo) {
            const userData = {
              name: user.displayName || additionalInfo.name,
              email: user.email,
              phone: additionalInfo.phone,
              location: additionalInfo.location,
              userType: additionalInfo.userType,
              sellerType: additionalInfo.userType === 'seller' ? additionalInfo.sellerType : null,
              createdAt: new Date().toISOString(),
              updatedAt: new Date().toISOString(),
              googleSignIn: true
            };

            // If seller, add approval status and additional fields
            if (additionalInfo.userType === 'seller') {
              userData.approved = false;
              userData.website = additionalInfo.website || '';
              userData.facebook = additionalInfo.facebook || '';
              userData.instagram = additionalInfo.instagram || '';
              userData.businessDescription = additionalInfo.businessDescription || '';
              userData.yearsInBusiness = additionalInfo.yearsInBusiness || '';
              userData.specializations = additionalInfo.specializations || '';
            }

            await setDoc(doc(db, 'users', user.uid), userData);

            // If seller, create seller review entry
            if (additionalInfo.userType === 'seller') {
              await setDoc(doc(db, 'sellerReviews', user.uid), {
                userId: user.uid,
                name: user.displayName || additionalInfo.name,
                email: user.email,
                phone: additionalInfo.phone,
                location: additionalInfo.location,
                sellerType: additionalInfo.sellerType,
                website: additionalInfo.website || '',
                facebook: additionalInfo.facebook || '',
                instagram: additionalInfo.instagram || '',
                businessDescription: additionalInfo.businessDescription || '',
                yearsInBusiness: additionalInfo.yearsInBusiness || '',
                specializations: additionalInfo.specializations || '',
                status: 'pending',
                submittedAt: new Date().toISOString(),
                reviewedAt: null,
                reviewedBy: null,
                notes: ''
              });
            }
          } else {
            // User cancelled additional info - sign them out
            await auth.signOut();
            setGoogleLoading(false);
            return;
          }
        }
      
      await fetchUserProfile(user);
      // The AuthContext will automatically update and redirect to marketplace
      onSignUpSuccess();
    } catch (error) {
      console.error('Google sign-in error:', error);
      switch (error.code) {
        case 'auth/popup-closed-by-user':
          setError('Sign-in was cancelled. Please try again.');
          break;
        case 'auth/popup-blocked':
          setError('Pop-up was blocked. Please allow pop-ups for this site.');
          break;
        case 'auth/cancelled-popup-request':
          setError('Sign-in was cancelled. Please try again.');
          break;
        default:
          setError('Google sign-in failed. Please try again.');
      }
    } finally {
      setGoogleLoading(false);
    }
  };

  const promptForAdditionalInfo = (user) => {
    return new Promise((resolve) => {
      const modal = document.createElement('div');
      modal.className = 'fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50';
      
      const modalContent = document.createElement('div');
      modalContent.className = 'bg-white rounded-xl p-6 max-w-2xl w-full mx-4 max-h-[90vh] overflow-y-auto';
      
      const formData = {
        name: user.displayName || '',
        phone: '',
        location: '',
        userType: 'buyer',
        sellerType: 'individual',
        website: '',
        facebook: '',
        instagram: '',
        businessDescription: '',
        yearsInBusiness: '',
        specializations: ''
      };
      
      let formHTML = `
        <div class="text-center mb-6">
          <h3 class="text-xl font-bold text-abiu-green-dark mb-2">Complete Your Profile</h3>
          <p class="text-gray-600">Please provide some additional information to complete your account setup.</p>
        </div>
        <form id="additionalInfoForm" class="space-y-4">
          <!-- Basic Information Row -->
          <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label class="block text-sm font-medium text-gray-700 mb-1">Full Name *</label>
              <input type="text" name="name" value="${formData.name}" required 
                     class="w-full px-4 py-2 rounded-lg border border-gray-200 focus:outline-none focus:ring-2 focus:ring-abiu-green">
            </div>
            <div>
              <label class="block text-sm font-medium text-gray-700 mb-1">Phone Number *</label>
              <input type="tel" name="phone" required 
                     class="w-full px-4 py-2 rounded-lg border border-gray-200 focus:outline-none focus:ring-2 focus:ring-abiu-green"
                     placeholder="Enter your phone number">
            </div>
          </div>
          
          <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label class="block text-sm font-medium text-gray-700 mb-1">Location *</label>
              <input type="text" name="location" required 
                     class="w-full px-4 py-2 rounded-lg border border-gray-200 focus:outline-none focus:ring-2 focus:ring-abiu-green"
                     placeholder="Enter your location (e.g., Lagos, Nigeria)">
            </div>
            <div>
              <label class="block text-sm font-medium text-gray-700 mb-1">I am a *</label>
              <select name="userType" class="w-full px-4 py-2 rounded-lg border border-gray-200 focus:outline-none focus:ring-2 focus:ring-abiu-green">
                <option value="buyer">Buyer</option>
                <option value="seller">Seller</option>
              </select>
            </div>
          </div>
          
          <div id="sellerTypeDiv" style="display: none;">
            <label class="block text-sm font-medium text-gray-700 mb-1">Seller Type *</label>
            <select name="sellerType" class="w-full px-4 py-2 rounded-lg border border-gray-200 focus:outline-none focus:ring-2 focus:ring-abiu-green">
              <option value="individual">Individual</option>
              <option value="business">Business</option>
            </select>
          </div>
          
          <div id="sellerFieldsDiv" style="display: none;">
            <div class="space-y-4">
              <div>
                <label class="block text-sm font-medium text-gray-700 mb-1">Business Description *</label>
                <textarea name="businessDescription" rows="3" 
                         class="w-full px-4 py-2 rounded-lg border border-gray-200 focus:outline-none focus:ring-2 focus:ring-abiu-green"
                         placeholder="Describe your business and services..."></textarea>
              </div>
              
              <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label class="block text-sm font-medium text-gray-700 mb-1">Years in Business</label>
                  <input type="number" name="yearsInBusiness" 
                         class="w-full px-4 py-2 rounded-lg border border-gray-200 focus:outline-none focus:ring-2 focus:ring-abiu-green"
                         placeholder="e.g., 5">
                </div>
                <div>
                  <label class="block text-sm font-medium text-gray-700 mb-1">Specializations</label>
                  <input type="text" name="specializations" 
                         class="w-full px-4 py-2 rounded-lg border border-gray-200 focus:outline-none focus:ring-2 focus:ring-abiu-green"
                         placeholder="e.g., Residential Solar, Commercial Installations">
                </div>
              </div>
              
              <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label class="block text-sm font-medium text-gray-700 mb-1">Website</label>
                  <input type="url" name="website" 
                         class="w-full px-4 py-2 rounded-lg border border-gray-200 focus:outline-none focus:ring-2 focus:ring-abiu-green"
                         placeholder="https://yourwebsite.com">
                </div>
                <div>
                  <label class="block text-sm font-medium text-gray-700 mb-1">Facebook Page</label>
                  <input type="text" name="facebook" 
                         class="w-full px-4 py-2 rounded-lg border border-gray-200 focus:outline-none focus:ring-2 focus:ring-abiu-green"
                         placeholder="Your Facebook page or profile">
                </div>
              </div>
              
              <div>
                <label class="block text-sm font-medium text-gray-700 mb-1">Instagram Handle</label>
                <input type="text" name="instagram" 
                       class="w-full px-4 py-2 rounded-lg border border-gray-200 focus:outline-none focus:ring-2 focus:ring-abiu-green"
                       placeholder="@yourinstagram">
              </div>
            </div>
          </div>
          
          <div class="flex gap-3 mt-8 pb-4">
            <button type="button" id="cancelBtn" 
                    class="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50">
              Cancel
            </button>
            <button type="submit" 
                    class="flex-1 bg-abiu-green text-white px-4 py-2 rounded-lg hover:bg-abiu-green-dark">
              Continue
            </button>
          </div>
        </form>
      `;
      
      modalContent.innerHTML = formHTML;
      modal.appendChild(modalContent);
      document.body.appendChild(modal);
      
      const form = modalContent.querySelector('#additionalInfoForm');
      const cancelBtn = modalContent.querySelector('#cancelBtn');
      const userTypeSelect = modalContent.querySelector('select[name="userType"]');
      const sellerTypeDiv = modalContent.querySelector('#sellerTypeDiv');
      const sellerFieldsDiv = modalContent.querySelector('#sellerFieldsDiv');
      
      // Initialize required attributes
      const businessDescriptionField = form.querySelector('textarea[name="businessDescription"]');
      if (businessDescriptionField) {
        businessDescriptionField.required = userTypeSelect.value === 'seller';
      }
      
      // Show/hide seller fields based on user type
      userTypeSelect.addEventListener('change', (e) => {
        const isSeller = e.target.value === 'seller';
        sellerTypeDiv.style.display = isSeller ? 'block' : 'none';
        sellerFieldsDiv.style.display = isSeller ? 'block' : 'none';
        
        // Update required attributes
        const businessDescriptionField = form.querySelector('textarea[name="businessDescription"]');
        if (businessDescriptionField) {
          businessDescriptionField.required = isSeller;
        }
      });
      
      form.addEventListener('submit', (e) => {
        e.preventDefault();
        const formData = new FormData(form);
        const userType = formData.get('userType');
        
        // Validate required fields
        if (!formData.get('name') || !formData.get('phone') || !formData.get('location')) {
          alert('Please fill in all required fields');
          return;
        }
        
        // Validate business description for sellers
        if (userType === 'seller' && !formData.get('businessDescription')) {
          alert('Please provide a business description');
          return;
        }
        
        resolve({
          name: formData.get('name'),
          phone: formData.get('phone'),
          location: formData.get('location'),
          userType: userType,
          sellerType: formData.get('sellerType'),
          website: formData.get('website'),
          facebook: formData.get('facebook'),
          instagram: formData.get('instagram'),
          businessDescription: formData.get('businessDescription'),
          yearsInBusiness: formData.get('yearsInBusiness'),
          specializations: formData.get('specializations')
        });
        document.body.removeChild(modal);
      });
      
      cancelBtn.addEventListener('click', () => {
        resolve(null);
        document.body.removeChild(modal);
      });
    });
  };

  return (
    <div className="min-h-screen flex flex-col items-center justify-center bg-abiu-green-bg px-4 py-8">
      <div className="bg-white rounded-xl shadow-lg p-8 max-w-md w-full animate-fadeIn">
        <div className="text-center mb-6">
          <img src="/logo192.png" alt="Abiu Logo" className="h-16 w-16 mx-auto mb-4 rounded-lg" />
          <h1 className="text-3xl font-bold text-abiu-green-dark mb-2">
            {isSignUp ? 'Create Account' : 'Welcome Back'}
          </h1>
          <p className="text-gray-700">
            {isSignUp ? 'Join Nigeria\'s Solar & Energy Marketplace' : 'Sign in to your account'}
          </p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          {/* Email */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Email *</label>
            <input
              type="email"
              name="email"
              value={formData.email}
              onChange={handleInputChange}
              className="w-full px-4 py-2 rounded-lg border border-gray-200 focus:outline-none focus:ring-2 focus:ring-abiu-green"
              placeholder="Enter your email"
              required
            />
          </div>

          {/* Password */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Password *</label>
            <input
              type="password"
              name="password"
              value={formData.password}
              onChange={handleInputChange}
              className="w-full px-4 py-2 rounded-lg border border-gray-200 focus:outline-none focus:ring-2 focus:ring-abiu-green"
              placeholder="Enter your password"
              required
            />
          </div>

          {/* Confirm Password (only for sign up) */}
          {isSignUp && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Confirm Password *</label>
              <input
                type="password"
                name="confirmPassword"
                value={formData.confirmPassword}
                onChange={handleInputChange}
                className="w-full px-4 py-2 rounded-lg border border-gray-200 focus:outline-none focus:ring-2 focus:ring-abiu-green"
                placeholder="Confirm your password"
                required
              />
            </div>
          )}

          {/* Name (only for sign up) */}
          {isSignUp && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Full Name *</label>
              <input
                type="text"
                name="name"
                value={formData.name}
                onChange={handleInputChange}
                className="w-full px-4 py-2 rounded-lg border border-gray-200 focus:outline-none focus:ring-2 focus:ring-abiu-green"
                placeholder="Enter your full name"
                required
              />
            </div>
          )}

          {/* Phone (only for sign up) */}
          {isSignUp && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Phone Number *</label>
              <input
                type="tel"
                name="phone"
                value={formData.phone}
                onChange={handleInputChange}
                className="w-full px-4 py-2 rounded-lg border border-gray-200 focus:outline-none focus:ring-2 focus:ring-abiu-green"
                placeholder="Enter your phone number"
                required
              />
            </div>
          )}

          {/* Location (only for sign up) */}
          {isSignUp && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Location *</label>
              <input
                type="text"
                name="location"
                value={formData.location}
                onChange={handleInputChange}
                className="w-full px-4 py-2 rounded-lg border border-gray-200 focus:outline-none focus:ring-2 focus:ring-abiu-green"
                placeholder="Enter your location (e.g., Lagos, Nigeria)"
                required
              />
            </div>
          )}

          {/* User Type (only for sign up) */}
          {isSignUp && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">I am a *</label>
              <select
                name="userType"
                value={formData.userType}
                onChange={handleInputChange}
                className="w-full px-4 py-2 rounded-lg border border-gray-200 focus:outline-none focus:ring-2 focus:ring-abiu-green"
                required
              >
                <option value="buyer">Buyer</option>
                <option value="seller">Seller</option>
              </select>
            </div>
          )}

          {/* Seller Type (only for sign up and if user is seller) */}
          {isSignUp && formData.userType === 'seller' && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Seller Type *</label>
              <select
                name="sellerType"
                value={formData.sellerType}
                onChange={handleInputChange}
                className="w-full px-4 py-2 rounded-lg border border-gray-200 focus:outline-none focus:ring-2 focus:ring-abiu-green"
                required
              >
                <option value="individual">Individual</option>
                <option value="business">Business</option>
              </select>
            </div>
          )}

          {/* Additional Seller Information */}
          {isSignUp && formData.userType === 'seller' && (
            <>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Business Description *</label>
                <textarea
                  name="businessDescription"
                  value={formData.businessDescription}
                  onChange={handleInputChange}
                  className="w-full px-4 py-2 rounded-lg border border-gray-200 focus:outline-none focus:ring-2 focus:ring-abiu-green"
                  placeholder="Describe your business and services..."
                  rows="3"
                  required={formData.userType === 'seller'}
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Years in Business</label>
                <input
                  type="number"
                  name="yearsInBusiness"
                  value={formData.yearsInBusiness}
                  onChange={handleInputChange}
                  className="w-full px-4 py-2 rounded-lg border border-gray-200 focus:outline-none focus:ring-2 focus:ring-abiu-green"
                  placeholder="e.g., 5"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Specializations</label>
                <input
                  type="text"
                  name="specializations"
                  value={formData.specializations}
                  onChange={handleInputChange}
                  className="w-full px-4 py-2 rounded-lg border border-gray-200 focus:outline-none focus:ring-2 focus:ring-abiu-green"
                  placeholder="e.g., Residential Solar, Commercial Installations"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Website</label>
                <input
                  type="url"
                  name="website"
                  value={formData.website}
                  onChange={handleInputChange}
                  className="w-full px-4 py-2 rounded-lg border border-gray-200 focus:outline-none focus:ring-2 focus:ring-abiu-green"
                  placeholder="https://yourwebsite.com"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Facebook Page</label>
                <input
                  type="text"
                  name="facebook"
                  value={formData.facebook}
                  onChange={handleInputChange}
                  className="w-full px-4 py-2 rounded-lg border border-gray-200 focus:outline-none focus:ring-2 focus:ring-abiu-green"
                  placeholder="Your Facebook page or profile"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Instagram Handle</label>
                <input
                  type="text"
                  name="instagram"
                  value={formData.instagram}
                  onChange={handleInputChange}
                  className="w-full px-4 py-2 rounded-lg border border-gray-200 focus:outline-none focus:ring-2 focus:ring-abiu-green"
                  placeholder="@yourinstagram"
                />
              </div>
            </>
          )}

          {/* Error Message */}
          {error && (
            <div className="text-red-500 text-sm text-center bg-red-50 p-3 rounded-lg">
              {error}
            </div>
          )}

          {/* Submit Button */}
          <button
            type="submit"
            disabled={loading}
            className="w-full bg-abiu-green text-white font-semibold py-3 rounded-lg hover:bg-abiu-green-dark transition disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {loading ? 'Processing...' : (isSignUp ? 'Create Account' : 'Sign In')}
          </button>
        </form>

        {/* Divider */}
        <div className="relative my-6">
          <div className="absolute inset-0 flex items-center">
            <div className="w-full border-t border-gray-300" />
          </div>
          <div className="relative flex justify-center text-sm">
            <span className="px-2 bg-white text-gray-500">Or continue with</span>
          </div>
        </div>

        {/* Google Sign In Button */}
        <button
          onClick={handleGoogleSignIn}
          disabled={googleLoading}
          className="w-full flex items-center justify-center gap-3 bg-white border border-gray-300 text-gray-700 font-semibold py-3 px-4 rounded-lg hover:bg-gray-50 transition disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {googleLoading ? (
            <div className="w-5 h-5 border-2 border-gray-300 border-t-abiu-green rounded-full animate-spin"></div>
          ) : (
            <svg className="w-5 h-5" viewBox="0 0 24 24">
              <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
              <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
              <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
              <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
            </svg>
          )}
          {googleLoading ? 'Signing in...' : 'Continue with Google'}
        </button>

        {/* Toggle between Sign Up and Sign In */}
        <div className="mt-6 text-center">
          <p className="text-gray-600">
            {isSignUp ? 'Already have an account?' : "Don't have an account?"}
            <button
              onClick={() => {
                setIsSignUp(!isSignUp);
                setError('');
                setFormData({
                  email: '',
                  password: '',
                  confirmPassword: '',
                  name: '',
                  phone: '',
                  location: '',
                  userType: 'buyer',
                  sellerType: 'individual'
                });
              }}
              className="ml-1 text-abiu-green hover:text-abiu-green-dark font-medium"
            >
              {isSignUp ? 'Sign In' : 'Sign Up'}
            </button>
          </p>
        </div>

        <div className="mt-6 text-xs text-gray-400 text-center">
          &copy; 2025 Abiu. All Rights Reserved
        </div>
      </div>
    </div>
  );
}

export default SignUp; 